import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import type { PromoGroup, PromoMatch } from '@/api/promotions';
import { cacheGet, cacheSet } from '@/lib/swrCache';
import { useComposer } from '@/components/comms/ComposerProvider';
import '@/styles/screens.css';
import '@/styles/promotions.css';

const WINDOW_DAYS = 30;
const MS_DAY = 86_400_000;

const eur = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' });
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });

/** Days from today (UTC-truncated) to an ISO date; negative = past. */
function daysFromToday(iso: string): number {
  const today = new Date(new Date().toISOString().slice(0, 10)).getTime();
  return Math.round((new Date(iso).getTime() - today) / MS_DAY);
}

function statusOf(g: { start_date: string; end_date: string }): { label: string; kind: 'live' | 'soon' | 'ending' } {
  const toStart = daysFromToday(g.start_date);
  const toEnd = daysFromToday(g.end_date);
  if (toStart > 0) return { label: toStart === 1 ? 'Starts tomorrow' : `Starts in ${toStart} days`, kind: 'soon' };
  if (toEnd <= 3) return { label: toEnd <= 0 ? 'Last day' : `Ends in ${toEnd} day${toEnd === 1 ? '' : 's'}`, kind: 'ending' };
  return { label: `Live · ${toEnd} days left`, kind: 'live' };
}

type Tab = 'promos' | 'customers';

/**
 * Promotions (SPEC-2): the 30-day runway. A promo matters because of WHO it's
 * for — every line carries "N of your customers", and the Customers tab inverts
 * the whole screen into a person-first outreach list with one-tap reminders.
 */
export function PromotionsPage() {
  const api = useApi();
  const [tab, setTab] = useState<Tab>('promos');
  const [groups, setGroups] = useState<PromoGroup[] | null>(null);
  const [matches, setMatches] = useState<PromoMatch[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const cachedG = cacheGet<PromoGroup[]>('promos');
    const cachedM = cacheGet<PromoMatch[]>('promo-matches');
    if (cachedG) setGroups(cachedG);
    if (cachedM) setMatches(cachedM);
    Promise.all([api.promotions(WINDOW_DAYS), api.promoMatches(WINDOW_DAYS)])
      .then(([g, m]) => {
        cacheSet('promos', g);
        cacheSet('promo-matches', m);
        setGroups(g);
        setMatches(m);
      })
      .catch(() => { if (!cachedG) setError(true); });
  }, [api]);

  const matchTotal = useMemo(
    () => new Set((matches ?? []).map((m) => m.customer_id)).size,
    [matches]
  );

  const scrollToGroup = useCallback((plannerNo: string) => {
    setTab('promos');
    // Let the tab render before scrolling.
    setTimeout(() => document.getElementById(`promo-${plannerNo}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  if (error) {
    return (
      <div className="screen">
        <p className="screen-greeting">Promotions</p>
        <h1 className="screen-title">Next {WINDOW_DAYS} days</h1>
        <p className="state-msg state-msg--error">Couldn&rsquo;t load promotions.</p>
      </div>
    );
  }

  return (
    <div className="screen">
      <p className="screen-greeting">Promotions</p>
      <div className="promo-head">
        <h1 className="screen-title">Next {WINDOW_DAYS} days</h1>
        <div className="segment-chips" role="tablist" aria-label="Promotions view">
          <button
            type="button" role="tab" aria-selected={tab === 'promos'}
            className={`segment-chip${tab === 'promos' ? ' segment-chip--active' : ''}`}
            onClick={() => setTab('promos')}
          >
            By promotion{groups ? ` (${groups.length})` : ''}
          </button>
          <button
            type="button" role="tab" aria-selected={tab === 'customers'}
            className={`segment-chip${tab === 'customers' ? ' segment-chip--active' : ''}`}
            onClick={() => setTab('customers')}
          >
            Your customers{matches ? ` (${matchTotal})` : ''}
          </button>
        </div>
      </div>

      {!groups && (
        <div className="result-list" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton-row" style={{ height: 64 }} />)}
        </div>
      )}

      {groups && groups.length === 0 && (
        <p className="state-msg">No promotions in the next {WINDOW_DAYS} days.</p>
      )}

      {groups && groups.length > 0 && (
        <>
          <Runway groups={groups} onPick={scrollToGroup} />
          {tab === 'promos' && <PromoGroups groups={groups} matches={matches ?? []} />}
          {tab === 'customers' && <MatchesList matches={matches} />}
        </>
      )}
    </div>
  );
}

// ── The runway: each promo is a bar on a real 30-day axis ────────────────────

function Runway({ groups, onPick }: { groups: PromoGroup[]; onPick: (plannerNo: string) => void }) {
  const pct = (iso: string) => Math.min(100, Math.max(0, (daysFromToday(iso) / WINDOW_DAYS) * 100));
  const weekTicks = [0, 7, 14, 21, 28];
  return (
    <div className="runway" aria-label="Promotion timeline">
      <div className="runway-axis">
        {weekTicks.map((d) => (
          <span key={d} className="runway-tick" style={{ left: `${(d / WINDOW_DAYS) * 100}%` }}>
            {d === 0 ? 'Today' : fmtDay(new Date(Date.now() + d * MS_DAY).toISOString().slice(0, 10))}
          </span>
        ))}
        <span className="runway-today" style={{ left: 0 }} />
      </div>
      {groups.map((g) => {
        const st = statusOf(g);
        const left = pct(g.start_date);
        const width = Math.max(3, pct(g.end_date) - left);
        return (
          <button
            key={g.planner_no}
            type="button"
            className={`runway-bar runway-bar--${st.kind}`}
            style={{ left: `${left}%`, width: `${width}%` }}
            title={`${g.promotion ?? g.planner_no} · ${fmtDay(g.start_date)} – ${fmtDay(g.end_date)}`}
            onClick={() => onPick(g.planner_no)}
          >
            <span className="runway-bar-label">{g.promotion ?? g.promo_type ?? g.planner_no}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── By promotion: grouped cards with per-line customer matches ───────────────

function PromoGroups({ groups, matches }: { groups: PromoGroup[]; matches: PromoMatch[] }) {
  const [openLine, setOpenLine] = useState<string | null>(null);
  const byItem = useMemo(() => {
    const m = new Map<string, PromoMatch[]>();
    for (const x of matches) {
      const arr = m.get(x.item_no) ?? [];
      arr.push(x);
      m.set(x.item_no, arr);
    }
    return m;
  }, [matches]);

  return (
    <div>
      {groups.map((g) => {
        const st = statusOf(g);
        return (
          <section
            key={g.planner_no}
            id={`promo-${g.planner_no}`}
            className="section promo-card"
          >
            <div className="promo-card-head">
              <div>
                <p className="promo-name">{g.promotion ?? g.promo_type ?? g.planner_no}</p>
                <p className="promo-dates">{fmtDay(g.start_date)} → {fmtDay(g.end_date)}</p>
              </div>
              <span className={`promo-status promo-status--${st.kind}`}>{st.label}</span>
            </div>
            <div className="promo-lines">
              {g.lines.map((l) => {
                const key = `${l.planner_no}|${l.line_no}`;
                const lineMatches = l.item_no ? (byItem.get(l.item_no) ?? []) : [];
                return (
                  <div key={key} className="promo-line">
                    <button
                      type="button"
                      className="promo-line-row"
                      onClick={() => setOpenLine(openLine === key ? null : key)}
                      disabled={lineMatches.length === 0}
                    >
                      <span className="promo-line-product">
                        <span className="promo-line-name">{l.description ?? l.item_no}</span>
                        <span className="promo-line-meta">
                          {[l.item_no, l.vintage, l.country_name].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      {l.floor_plan && <span className="promo-pos">{l.floor_plan}</span>}
                      <span className="promo-price">
                        {l.was_price != null && <s>{eur.format(l.was_price)}</s>}
                        {l.promo_price != null && <strong>{eur.format(l.promo_price)}</strong>}
                      </span>
                      {l.percent_saving != null && l.percent_saving > 0 && (
                        <span className="promo-off">−{Math.round(l.percent_saving)}%</span>
                      )}
                      <span className={`promo-match-chip${lineMatches.length ? '' : ' promo-match-chip--zero'}`}>
                        {lineMatches.length
                          ? `${lineMatches.length} customer${lineMatches.length === 1 ? '' : 's'}`
                          : 'No matches'}
                      </span>
                    </button>
                    {openLine === key && lineMatches.length > 0 && (
                      <MatchRows matches={lineMatches} compact />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ── Your customers: the person-first outreach list ───────────────────────────

function MatchesList({ matches }: { matches: PromoMatch[] | null }) {
  if (!matches) return <div className="skeleton-row" style={{ height: 64 }} />;
  if (matches.length === 0) {
    return (
      <p className="state-msg">
        No customer favourites match upcoming promotions yet. Tag favourites on
        customer cards and this list fills itself.
      </p>
    );
  }
  return (
    <section className="section">
      <p className="section-title">Customers to call — favourites going on promotion</p>
      <MatchRows matches={matches} />
    </section>
  );
}

function MatchRows({ matches, compact }: { matches: PromoMatch[]; compact?: boolean }) {
  const api = useApi();
  const navigate = useNavigate();
  const composer = useComposer();
  const [done, setDone] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  const keyOf = (m: PromoMatch) => `${m.customer_id}|${m.item_no}`;

  async function remind(m: PromoMatch) {
    const k = keyOf(m);
    setBusy(k);
    try {
      await api.addPromoReminder({
        customerId: m.customer_id,
        plannerNo: m.planner_no,
        itemNo: m.item_no,
      });
      setDone((s) => new Set(s).add(k));
    } catch { /* leave button re-tappable */ } finally {
      setBusy(null);
    }
  }

  return (
    <div className={`match-list${compact ? ' match-list--compact' : ''}`}>
      {matches.map((m) => {
        const k = keyOf(m);
        const reminded = m.has_reminder || done.has(k);
        const toStart = daysFromToday(m.start_date);
        return (
          <div key={k} className="match-row">
            <span className="match-who">
              <span className="match-name">{[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}</span>
              <span className="match-meta">{m.store_code}</span>
            </span>
            <span className="match-what">
              <span className="match-product">{m.product_name ?? m.item_no}</span>
              <span className="match-deal">
                {m.was_price != null && <s>{eur.format(m.was_price)}</s>}{' '}
                {m.promo_price != null && <strong>{eur.format(m.promo_price)}</strong>}
                {m.percent_saving != null && m.percent_saving > 0 && ` (−${Math.round(m.percent_saving)}%)`}
                {m.floor_plan ? ` · ${m.floor_plan}` : ''}
              </span>
            </span>
            <span className="match-when">
              {toStart > 0 ? `starts ${fmtDay(m.start_date)}` : `until ${fmtDay(m.end_date)}`}
            </span>
            <span className="match-actions">
              <button
                type="button"
                className={`btn btn--sm${reminded ? ' btn--ghost' : ''}`}
                disabled={reminded || busy === k}
                onClick={() => remind(m)}
              >
                {reminded ? '✓ Reminder set' : busy === k ? 'Saving…' : toStart > 0 ? `Remind ${fmtDay(m.start_date)}` : 'Remind me'}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                title="Email this customer about the offer"
                onClick={() => composer.openEmail([{
                  customer_id: m.customer_id,
                  name: [m.first_name, m.last_name].filter(Boolean).join(' ') || '—',
                  email: null, // resolved server-side; guard reports if missing
                }])}
              >
                Email
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate(`/customers/${m.customer_id}`)}>
                Open
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
